import { MapView } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import { load } from "@loaders.gl/core";
import { KMLLoader } from '@loaders.gl/kml';
import ExploreIcon from '@mui/icons-material/Explore';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import HeightIcon from '@mui/icons-material/Height';
import PlaceIcon from '@mui/icons-material/Place';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SpeedIcon from '@mui/icons-material/Speed';
import StoreIcon from '@mui/icons-material/Store';
import { Alert, Box, Button, CircularProgress, Grid, Paper, SvgIcon, Typography, useMediaQuery, useTheme } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import { blue } from '@mui/material/colors';
import { convertMetersToFeetAltitude, convertMetersToFeetSpeed, Display, formatLatitude, formatLongitude, GeoInformation, MapStyle, OrderInfo, Port, ReceiveStatus } from 'adoms-common-lib';
import { Auth, Geo } from 'aws-amplify';
import type { FeatureCollection } from 'geojson';
import * as geolib from "geolib";
import React, { useEffect, useMemo } from 'react';
import { Layer, LineLayer, Marker, NavigationControl, Popup, Source } from 'react-map-gl';
import { ReactComponent as DroneIcon } from "../../asset/icons/quadcopter.svg";
import { APIConnector } from '../../connector/APIConnector';
import InfoRow from '../atoms/InfoRow';

type AmplifyGeoConfig = {
    geo?: {
        amazon_location_service?: AmplifyGeoOptions;
        AmazonLocationService?: AmplifyGeoOptions;
    };
};

type AmplifyGeoOptions = {
    maps?: {
        neutral: string,
        satellite: string
    };
    region: string;
};

type DroneMapViewProps = {
    order: OrderInfo | undefined
    tenantId: string | undefined
}

type GeoInformationPerFlightId = {
    flightId: string,
    geoInformationList: GeoInformation[]
};

type LatestGeoInformationPerFlightId = {
    flightId: string,
    geoInformation: GeoInformation
};

/**
 * ドローン位置情報マップを表示する
 * @param props order
 */
export const DroneMapView: React.FC<DroneMapViewProps> = (props) => {

    const amplifyConfig = Geo.configure() as AmplifyGeoConfig;
    Auth.configure();

    const geoConfig = useMemo(
        () =>
            amplifyConfig.geo?.amazon_location_service ??
            amplifyConfig.geo?.AmazonLocationService ??
            ({} as AmplifyGeoOptions),
        [amplifyConfig]
    );
    const theme = useTheme();
    const isBelowMapLayout = useMediaQuery(theme.breakpoints.down('lg'));
    const styleProps = useMemo(
        () => ({
            height: "350px",
            position: 'relative',
            width: '100%'
        }),
        []
    );
    type MinMaxLanLng = {
        minLat: number,
        maxLat: number,
        minLng: number,
        maxLng: number
    };
    const [mapErrorMessage, setMapErrorMessage] = React.useState<string | undefined>();
    const [geoConfigMapStyle, setGeoConfigMapStyle] = React.useState<string | undefined>(geoConfig.maps?.neutral);
    const [geoInformationByOrderIdErrorMessage, setGeoInformationByOrderIdErrorMessage] = React.useState<string | undefined>();
    const [geoInformationListPerFlightIdList, setGeoInformationListPerFlightIdList] = React.useState<GeoInformationPerFlightId[]>([]);
    const [mapStyle, setMapStyle] = React.useState<MapStyle>(MapStyle.neutral);
    const [departurePortPopupInfo, setDeparturePortPopupInfo] = React.useState<Port | undefined>();
    const [arrivalPortPopupInfo, setArrivalPortPopupInfo] = React.useState<Port | undefined>();
    const [dronePopupInfo, setDronePopupInfo] = React.useState<GeoInformation | undefined>();
    const [minMaxLanLng, setMinMaxLanLng] = React.useState<MinMaxLanLng | undefined>(undefined);
    const [routeData, setRouteData] = React.useState<FeatureCollection | undefined>();
    const [hasCompletedInitialGeoFetch, setHasCompletedInitialGeoFetch] = React.useState<boolean>(false);

    const calculateBoundsFromRouteData = (routeGeoJson: FeatureCollection): MinMaxLanLng | undefined => {
        const coordinates: {
            longitude: number;
            latitude: number;
        }[] = [];

        routeGeoJson.features.forEach((feature) => {
            const geometry = feature.geometry as any;
            if (!geometry) {
                return;
            }

            if (geometry.type === "LineString") {
                geometry.coordinates.forEach((coordinate: number[]) => {
                    coordinates.push({
                        longitude: coordinate[0],
                        latitude: coordinate[1]
                    });
                });
            }
        });

        if (coordinates.length === 0) {
            return undefined;
        }

        return geolib.getBounds(coordinates);
    };

    /**
     * 画面表示時に地図の中心となる点を算出する
     * また、ドローンの位置情報を取得する
     */
    useEffect(() => {
        if (props.order) {
            const minMaxLanLngParam = geolib.getBounds([
                {
                    longitude: Number(props.order.departure.longitude),
                    latitude: Number(props.order.departure.latitude)
                },
                {
                    longitude: Number(props.order.arrival.longitude),
                    latitude: Number(props.order.arrival.latitude)
                },
            ]);
            if (!minMaxLanLngParam) {
                // 出発地点と到着地点の中間地点の座標取得に失敗した場合
                setMapErrorMessage("地図表示地点を取得できませんでした");
            } else {
                setMapErrorMessage(undefined);
                setMinMaxLanLng(minMaxLanLngParam);
            };
            const dataload = async () => {
                // 表示させるルートを取得する
                const kmlFilePath = "./kml/orderAndRequesterView/" + props.order?.businessPartnerId
                    + "_" + props.order?.departure.id
                    + "_" + props.order?.arrival.id + ".kml";
                try {
                    //KMLデータを読み込む
                    const res = await load(kmlFilePath, KMLLoader);
                    //KMLローダーで読み込んだデータはGeoJSON形式に変換されている
                    console.log(res);
                    //読み込んだデータを保存
                    setRouteData(res);

                    const routeBounds = calculateBoundsFromRouteData(res as FeatureCollection);
                    if (routeBounds) {
                        setMinMaxLanLng(routeBounds);
                    } else if (minMaxLanLngParam) {
                        setMinMaxLanLng(minMaxLanLngParam);
                    }
                } catch (error) {
                    console.log(error);
                    setRouteData(undefined);
                    if (minMaxLanLngParam) {
                        setMinMaxLanLng(minMaxLanLngParam);
                    }
                }
            };
            dataload();
            fetchGeoInformationByFlightId(true);
        };
    }, [props.order]);

    /**
     * 配送が完了、またはキャンセルされていない場合、
     * 5秒間隔でドローンの位置情報を取得する
     */
    useEffect(() => {
        if (props.order?.receiveStatus !== ReceiveStatus.Received
            && props.order?.receiveStatus !== ReceiveStatus.Cancel) {
            let timeoutId: number | undefined
            // 5秒後に位置情報を再取得する（クリーンアップ時にclearTimeoutで解除）
            timeoutId = window.setTimeout(() => fetchGeoInformationByFlightId(false), 5 * 1000);
            return () => {
                if (typeof timeoutId !== "undefined") {
                    window.clearTimeout(timeoutId);
                };
            };
        }
    }, [geoInformationListPerFlightIdList]);

    /**
     * フライトIDごとのドローンの位置情報を取得する
     */
    const fetchGeoInformationByFlightId = async (isInitialFetch: boolean = false) => {
        if (document.hasFocus()) {
            // 画面がアクティブの場合
            let c: APIConnector = APIConnector.instance;
            if (props.order && props.tenantId) {
                await c.getGeoInformationByOrderId(props.order.orderID, props.tenantId).then((geoInformationPerFlightIdMap: Map<string, GeoInformation[]>) => {
                    console.log(geoInformationPerFlightIdMap);
                    setGeoInformationByOrderIdErrorMessage(undefined);
                    let geoInformationListPerFlightIdList: GeoInformationPerFlightId[] = new Array();
                    geoInformationPerFlightIdMap.forEach(function (geoInformation, flightId) {
                        if (geoInformation) {
                            let geoInformationListPerFlightId = {
                                flightId: flightId,
                                geoInformationList: geoInformation
                            };
                            geoInformationListPerFlightIdList.push(geoInformationListPerFlightId);
                        };
                    });
                    setGeoInformationListPerFlightIdList(geoInformationListPerFlightIdList);
                    if (isInitialFetch) {
                        setHasCompletedInitialGeoFetch(true);
                    }
                }).catch((error) => {
                    console.log(error);
                    setGeoInformationByOrderIdErrorMessage("ドローン位置情報を取得できませんでした。");
                    if (isInitialFetch) {
                        setHasCompletedInitialGeoFetch(true);
                    }
                });
            };
        } else {
            setGeoInformationListPerFlightIdList([...geoInformationListPerFlightIdList]);
        }
    };

    /**
     * ドローンの経路（マーカー）の色をグラデーションで表示する。
     * 先頭のマーカーはドローンのアイコンを表示させる。
     * @param locationIndex 
     */
    const handleDisplayLocationIcon = (locationIndex: number) => {

        if (locationIndex === 0) {
            return (
                <SvgIcon
                    sx={{
                        color: mapStyle === MapStyle.satellite ? "#ffffff" : "#000000",
                        stroke: mapStyle === MapStyle.satellite ? "#000000" : "#ffffff",
                        strokeWidth: "0.5px"
                    }}>
                    <DroneIcon />
                </SvgIcon >
            );
        } else {
            return (
                <FiberManualRecordIcon
                    sx={{ color: "#e06666", fontSize: 10 }} />
            );
        };
    };

    /**
     * ドローン用ポップアップを表示させる
     */
    const displayDronePopupInfo = () => {
        if (dronePopupInfo) {
            let sendTime = Display.getTimeMiniteSecondStringFromISO8601(dronePopupInfo.sendTime);
            return (
                <Popup
                    longitude={dronePopupInfo.longitude}
                    latitude={dronePopupInfo.latitude}
                    anchor="bottom"
                    onClose={() => setDronePopupInfo(undefined)}
                    style={{ zIndex: 2600 }}>
                    <Grid container direction="row">
                        <ScheduleIcon fontSize="small" sx={{ marginRight: "2px" }} />
                        <Typography variant='subtitle2'>{sendTime}</Typography>
                    </Grid>
                </Popup>
            );
        };
    };

    /**
     * ポート用ポップアップを表示させる
     */
    const displayPortPopupInfo = () => {

        if (departurePortPopupInfo) {
            return (
                <Popup
                    longitude={Number(departurePortPopupInfo.longitude)}
                    latitude={Number(departurePortPopupInfo.latitude)}
                    anchor="bottom"
                    onClose={() => setDeparturePortPopupInfo(undefined)}
                    style={{ zIndex: 2600 }}>
                    <Typography variant='subtitle2' sx={{ color: "#2196f3" }}>{"出発地"}</Typography>
                    <Typography variant='subtitle1' fontWeight={"bold"} sx={{ marginBottom: "3px" }}>
                        {departurePortPopupInfo.name}
                    </Typography>
                    <Typography variant='subtitle2'>
                        〒{departurePortPopupInfo.zipcode}
                    </Typography>
                    <Typography variant='subtitle2'>
                        {departurePortPopupInfo.prefecture}{departurePortPopupInfo.address}
                    </Typography>
                </Popup>
            );
        } else if (arrivalPortPopupInfo) {
            return (
                <Popup
                    longitude={Number(arrivalPortPopupInfo.longitude)}
                    latitude={Number(arrivalPortPopupInfo.latitude)}
                    anchor="bottom"
                    onClose={() => setArrivalPortPopupInfo(undefined)}
                    style={{ zIndex: 2600 }}>
                    <Typography variant='subtitle2' sx={{ color: "#2196f3" }}>{"到着地"}</Typography>
                    <Typography variant='subtitle1' fontWeight={"bold"} sx={{ marginBottom: "3px" }}>
                        {arrivalPortPopupInfo.name}
                    </Typography>
                    <Typography variant='subtitle2'>
                        〒{arrivalPortPopupInfo.zipcode}
                    </Typography>
                    <Typography variant='subtitle2'>
                        {arrivalPortPopupInfo.prefecture}{arrivalPortPopupInfo.address}
                    </Typography>
                </Popup>
            );
        };
    };

    /**
     * 地図の表示形式が変更された時のハンドラ
     * @param mapStyle 
     */
    const handleChangeMapStyle = (mapStyle: MapStyle) => {
        setMapStyle(mapStyle);
        if (mapStyle === MapStyle.neutral) {
            setGeoConfigMapStyle(geoConfig.maps?.neutral);
        } else if (mapStyle === MapStyle.satellite) {
            setGeoConfigMapStyle(geoConfig.maps?.satellite);
        };
    };

    const lineLayerStyle: LineLayer = {
        id: 'line',
        type: 'line',
        paint: {
            "line-color": "#ea9245",
            "line-width": 3
        }
    };

    const latestGeoInformationList = useMemo<LatestGeoInformationPerFlightId[]>(() => {
        return geoInformationListPerFlightIdList
            .map((geoInfoPerFlightId) => ({
                flightId: geoInfoPerFlightId.flightId,
                geoInformation: geoInfoPerFlightId.geoInformationList[0]
            }))
            .filter((geoInfoPerFlightId): geoInfoPerFlightId is LatestGeoInformationPerFlightId => {
                return geoInfoPerFlightId.geoInformation !== undefined;
            });
    }, [geoInformationListPerFlightIdList]);

    const displayAltitude = (altitude?: number) => {
        if (altitude === undefined || altitude === null) {
            return "ー";
        }
        const feet = convertMetersToFeetAltitude(altitude);
        return `${altitude} m / ${feet} ft`;
    };

    const displaySendTime = (sendTime?: string) => {
        const formattedSendTime = Display.getDateTimeSecondSlashStringFromISO8601(sendTime);
        if (formattedSendTime === "未定義") {
            return "ー";
        }
        return formattedSendTime;
    };

    const displayReceivedBefore = (sendTime?: string) => {
        if (!sendTime || props.order?.receiveStatus === ReceiveStatus.Received) {
            return undefined;
        }

        const sendTimeDate = new Date(sendTime);
        if (Number.isNaN(sendTimeDate.getTime())) {
            return undefined;
        }

        const now = new Date();
        const diffSeconds = Math.max(0, Math.floor((now.getTime() - sendTimeDate.getTime()) / 1000));
        if (diffSeconds <= 59) {
            return `${diffSeconds}秒前`;
        }

        if (diffSeconds < (60 * 60)) {
            const minutesBefore = Math.max(1, Math.floor(diffSeconds / 60))
            return `${minutesBefore}分前`;
        }

        if (diffSeconds < (24 * 60 * 60)) {
            const hoursBefore = Math.max(1, Math.floor(diffSeconds / 3600));
            return `${hoursBefore}時間前`;
        }

        return undefined;
    };

    const displayReceivedInfo = (sendTime?: string) => {
        const receivedBefore = displayReceivedBefore(sendTime);
        if (!receivedBefore) {
            return undefined;
        }
        return `${displaySendTime(sendTime)} (${receivedBefore})`;
    };

    const displayHeading = (heading?: number) => {
        if (heading === undefined || heading === null) {
            return "ー";
        }
        return `${heading}°`;
    };

    const displayLatitude = (latitude?: number) => {
        if (latitude === undefined || latitude === null) {
            return "ー";
        }
        return `${latitude} (${formatLatitude(latitude)})`;
    };

    const displayLongitude = (longitude?: number) => {
        if (longitude === undefined || longitude === null) {
            return "ー";
        }
        return `${longitude} (${formatLongitude(longitude)})`;
    };

    const displaySpeed = (speed?: number) => {
        if (speed === undefined || speed === null) {
            return "ー";
        }
        const speedKt = convertMetersToFeetSpeed(speed);
        return `${speed} m/s / ${speedKt} kt`;
    };

    const displayLabel = (icon: React.ReactElement, label: string) => {
        return (
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                {icon}
                {label}
            </Box>
        );
    };

    const renderGeoInformationPanel = () => {
        return (
            <Box
                sx={{
                    width: "100%",
                    maxHeight: { xs: "none", lg: "350px" },
                    overflowY: { xs: "visible", lg: "auto" }
                }}>
                <Paper
                    sx={{
                        padding: "8px",
                        borderColor: "grey.200",
                        backgroundColor: "rgba(255, 255, 255, 0.95)"
                    }}
                    elevation={2}
                    variant='outlined'>
                    <Grid container spacing={1} alignItems="center" sx={{ marginBottom: "4px" }}>
                        <Grid item>
                            <Avatar sx={{ bgcolor: blue[400], width: 28, height: 28 }}>
                                <GpsFixedIcon sx={{ fontSize: 16 }} />
                            </Avatar>
                        </Grid>
                        <Grid item xs>
                            <Typography variant='subtitle1' fontWeight={"bold"} sx={{ color: "#2196f3" }}>
                                ドローン位置情報
                            </Typography>
                            <Typography variant='subtitle2' sx={{ color: "#8b8989" }}>
                                直近の位置情報を表示しています
                            </Typography>
                        </Grid>
                    </Grid>
                    {
                        !hasCompletedInitialGeoFetch ?
                            <Grid container justifyContent="center" alignItems="center" sx={{ py: 2 }}>
                                <CircularProgress size={24} />
                            </Grid>
                            : latestGeoInformationList.length === 0 ?
                                <Typography variant='body2'>位置情報はありません。</Typography>
                                : latestGeoInformationList.map((latestGeoInformationPerFlightId, index) => (
                                    <Paper
                                        key={`latest-geo-panel-${latestGeoInformationPerFlightId.flightId}-${index}`}
                                        sx={{
                                            padding: "8px",
                                            marginBottom: index === latestGeoInformationList.length - 1 ? "0px" : "6px",
                                            borderColor: "grey.200",
                                        }}
                                        elevation={0}
                                        variant='outlined'>
                                        {isBelowMapLayout ?
                                            <Grid container spacing={1}>
                                                <Grid item xs={6}>
                                                    <InfoRow
                                                        label={displayLabel(<PlaceIcon sx={{ fontSize: 14, color: blue[400] }} />, "緯度")}
                                                        value={displayLatitude(latestGeoInformationPerFlightId.geoInformation.latitude)}
                                                    />
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <InfoRow
                                                        label={displayLabel(<PlaceIcon sx={{ fontSize: 14, color: blue[400] }} />, "経度")}
                                                        value={displayLongitude(latestGeoInformationPerFlightId.geoInformation.longitude)}
                                                    />
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <InfoRow
                                                        label={displayLabel(<HeightIcon sx={{ fontSize: 14, color: blue[400] }} />, "高度")}
                                                        value={displayAltitude(latestGeoInformationPerFlightId.geoInformation.altitude)}
                                                    />
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <InfoRow
                                                        label={displayLabel(<SpeedIcon sx={{ fontSize: 14, color: blue[400] }} />, "速度")}
                                                        value={displaySpeed(latestGeoInformationPerFlightId.geoInformation.speed)}
                                                    />
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <InfoRow
                                                        label={displayLabel(<ExploreIcon sx={{ fontSize: 14, color: blue[400] }} />, "方位角")}
                                                        value={displayHeading(latestGeoInformationPerFlightId.geoInformation.heading)}
                                                    />
                                                </Grid>
                                                {displayReceivedInfo(latestGeoInformationPerFlightId.geoInformation.sendTime) ?
                                                    <Grid item xs={6}>
                                                        <InfoRow
                                                            label={displayLabel(<ScheduleIcon sx={{ fontSize: 14, color: blue[400] }} />, "受信")}
                                                            value={displayReceivedInfo(latestGeoInformationPerFlightId.geoInformation.sendTime)}
                                                        />
                                                    </Grid>
                                                    : undefined}
                                            </Grid>
                                            :
                                            <React.Fragment>
                                                <InfoRow
                                                    label={displayLabel(<PlaceIcon sx={{ fontSize: 14, color: blue[400] }} />, "緯度")}
                                                    value={displayLatitude(latestGeoInformationPerFlightId.geoInformation.latitude)}
                                                />
                                                <InfoRow
                                                    label={displayLabel(<PlaceIcon sx={{ fontSize: 14, color: blue[400] }} />, "経度")}
                                                    value={displayLongitude(latestGeoInformationPerFlightId.geoInformation.longitude)}
                                                />
                                                <InfoRow
                                                    label={displayLabel(<HeightIcon sx={{ fontSize: 14, color: blue[400] }} />, "高度")}
                                                    value={displayAltitude(latestGeoInformationPerFlightId.geoInformation.altitude)}
                                                />
                                                <InfoRow
                                                    label={displayLabel(<SpeedIcon sx={{ fontSize: 14, color: blue[400] }} />, "速度")}
                                                    value={displaySpeed(latestGeoInformationPerFlightId.geoInformation.speed)}
                                                />
                                                <InfoRow
                                                    label={displayLabel(<ExploreIcon sx={{ fontSize: 14, color: blue[400] }} />, "方位角")}
                                                    value={displayHeading(latestGeoInformationPerFlightId.geoInformation.heading)}
                                                />
                                                {displayReceivedInfo(latestGeoInformationPerFlightId.geoInformation.sendTime) ?
                                                    <InfoRow
                                                        label={displayLabel(<ScheduleIcon sx={{ fontSize: 14, color: blue[400] }} />, "受信")}
                                                        value={displayReceivedInfo(latestGeoInformationPerFlightId.geoInformation.sendTime)}
                                                    /> : undefined}
                                            </React.Fragment>}
                                    </Paper>
                                ))
                    }
                </Paper>
            </Box>
        );
    };

    return (
        <React.Fragment>
            {
                mapErrorMessage ?
                    <Paper sx={{
                        padding: "10px",
                        marginBottom: "10px"
                    }} elevation={1} >
                        <Alert severity="error">{mapErrorMessage}</Alert>
                    </Paper>
                    : undefined
            }
            {
                geoInformationByOrderIdErrorMessage ?
                    <Paper sx={{
                        padding: "10px",
                        margin: "10px"
                    }} elevation={1} >
                        <Alert severity="error">{geoInformationByOrderIdErrorMessage}</Alert>
                    </Paper>
                    : undefined
            }
            {
                props.order && minMaxLanLng ?
                    <Grid container spacing={1.5} alignItems="stretch">
                        <Grid item xs={12} lg={4} order={{ xs: 2, lg: 1 }}>
                            {renderGeoInformationPanel()}
                        </Grid>
                        <Grid item xs={12} lg={8} order={{ xs: 1, lg: 2 }}>
                            <MapView
                                initialViewState={
                                    props.order.departure.id
                                        === props.order.arrival.id && !routeData ?
                                        {
                                            latitude: Number(props.order.departure.latitude),
                                            longitude: Number(props.order.departure.longitude),
                                            zoom: props.order.businessPartnerId === "AIRWINGS" ? 13 : 15
                                        }
                                        : {
                                            bounds:
                                                [
                                                    [minMaxLanLng.minLng, minMaxLanLng.minLat],
                                                    [minMaxLanLng.maxLng, minMaxLanLng.maxLat]],
                                            fitBoundsOptions: {
                                                padding: 60
                                            }
                                        }}
                                style={styleProps as React.CSSProperties}
                                onError={error => {
                                    console.log(error);
                                    if (error) {

                                    }
                                    setMapErrorMessage("地図情報を取得できませんでした。ページを更新してください。");
                                }}
                                mapStyle={geoConfigMapStyle}>
                                <Marker
                                    longitude={Number(props.order.departure.longitude)}
                                    latitude={Number(props.order.departure.latitude)}
                                    onClick={e => {
                                        e.originalEvent.stopPropagation();
                                        setArrivalPortPopupInfo(undefined);
                                        setDronePopupInfo(undefined);
                                        if (departurePortPopupInfo?.id === props.order?.departure.id) {
                                            setDeparturePortPopupInfo(undefined);
                                        } else {
                                            setDeparturePortPopupInfo(props.order?.departure);
                                        }
                                    }}>
                                    <Avatar sx={{ height: 30, width: 30, backgroundColor: blue[500] }}>
                                        <StoreIcon fontSize='small' />
                                    </Avatar>
                                </Marker>
                                <Marker
                                    longitude={Number(props.order.arrival.longitude)}
                                    latitude={Number(props.order.arrival.latitude)}
                                    onClick={e => {
                                        e.originalEvent.stopPropagation();
                                        setDeparturePortPopupInfo(undefined);
                                        setDronePopupInfo(undefined);
                                        if (arrivalPortPopupInfo?.id === props.order?.arrival.id) {
                                            setArrivalPortPopupInfo(undefined);
                                        } else {
                                            setArrivalPortPopupInfo(props.order?.arrival);
                                        }
                                    }}>
                                    <PlaceIcon fontSize='large' sx={{ color: "#dc143c" }} />
                                </Marker>
                                {geoInformationListPerFlightIdList.map((geoInformationListPerDroneIdId, droneIndex) => (
                                    (geoInformationListPerDroneIdId.geoInformationList).map((geoInformation, locationIndex) => (
                                        <Marker
                                            key={`marker-${droneIndex}-${locationIndex}`}
                                            longitude={geoInformation.longitude}
                                            latitude={geoInformation.latitude}
                                            style={{ zIndex: 2550 - locationIndex }}
                                            onClick={e => {
                                                e.originalEvent.stopPropagation();
                                                setDeparturePortPopupInfo(undefined);
                                                setArrivalPortPopupInfo(undefined);
                                                if (dronePopupInfo?.flightId === geoInformation.flightId
                                                    && dronePopupInfo?.sendTime === geoInformation.sendTime) {
                                                    setDronePopupInfo(undefined);
                                                } else {
                                                    setDronePopupInfo(geoInformation);
                                                }
                                            }}
                                        >
                                            {handleDisplayLocationIcon(locationIndex)}
                                        </Marker>
                                    ))
                                ))}
                                {displayPortPopupInfo()}
                                {displayDronePopupInfo()}
                                {mapStyle === MapStyle.satellite ?
                                    <Button
                                        sx={{ margin: "10px", minWidth: "0px" }}
                                        size='small'
                                        variant="contained"
                                        onClick={(e) => handleChangeMapStyle(MapStyle.neutral)}
                                    >
                                        地形
                                    </Button>
                                    :
                                    <Button
                                        sx={{ margin: "10px", minWidth: "0px" }}
                                        size='small'
                                        variant="contained"
                                        onClick={(e) => handleChangeMapStyle(MapStyle.satellite)}
                                    >
                                        衛星
                                    </Button>
                                }
                                <NavigationControl style={{ margin: "10px" }} />
                                {routeData ?
                                    <Source id="route" type="geojson" data={routeData}>
                                        <Layer {...lineLayerStyle} />
                                    </Source>
                                    : undefined}
                            </MapView>
                        </Grid>
                    </Grid >
                    : undefined
            }
        </React.Fragment >
    )
}